/**
 * ============================================================================
 * AGENDA VIEW v4.0 - PLANIVIO CSS CLASS-BASED
 * ============================================================================
 *
 * ✅ v4.0: CSS Class-Based Redesign
 *          - ALL inline styles → CSS classes (cal-agenda- namespace)
 *          - Matches Calendar.css v3.0 design system
 *          - Dark mode ready via parent .calendar-redesign.dark
 *          - Business logic 100% unchanged from v3.0
 *
 * ✅ v3.0: Planivio Rebranding (Orange → Teal)
 * ✅ v2.1: Header wie WeekView mit Monat und Jahr
 * ✅ v2.0: Warmes Design + Heller Hintergrund
 *
 * @version 4.0.0 - CSS Class-Based
 */

import React from 'react';
import {
  weekDays,
  monthNames,
  formatFullDate,
  isToday,
  getStartOfMonth,
  getEndOfMonth,
  getAppointmentsForDateRange,
  getFamilyMember
} from './calendarUtils';

const formatTimeCompact = (timeString) => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  return `${parts[0]}:${parts[1]}`;
};

const getCategoryStyle = (category) => {
  const styles = {
    'arzt':      { cssClass: 'medical',  icon: '🏥', name: 'Arzt' },
    'medical':   { cssClass: 'medical',  icon: '🏥', name: 'Arzt' },
    'schule':    { cssClass: 'school',   icon: '🎓', name: 'Schule' },
    'school':    { cssClass: 'school',   icon: '🎓', name: 'Schule' },
    'arbeit':    { cssClass: 'work',     icon: '💼', name: 'Arbeit' },
    'work':      { cssClass: 'work',     icon: '💼', name: 'Arbeit' },
    'privat':    { cssClass: 'personal', icon: '🏠', name: 'Privat' },
    'personal':  { cssClass: 'personal', icon: '🏠', name: 'Privat' },
    'sport':     { cssClass: 'sport',    icon: '⚽', name: 'Sport' },
    'familie':   { cssClass: 'family',   icon: '👨‍👩‍👧‍👦', name: 'Familie' },
    'family':    { cssClass: 'family',   icon: '👨‍👩‍👧‍👦', name: 'Familie' },
    'sonstiges': { cssClass: 'other',    icon: '📌', name: 'Sonstiges' },
    'default':   { cssClass: 'default',  icon: '📅', name: 'Termin' }
  };
  return styles[category?.toLowerCase()] || styles.default;
};

function AgendaView({ currentDate, appointments, onAppointmentClick }) {
  const startDate = getStartOfMonth(currentDate);
  const endDate = getEndOfMonth(currentDate);
  const monthAppointments = getAppointmentsForDateRange(appointments, startDate, endDate);

  const grouped = monthAppointments.reduce((acc, apt) => {
    if (!acc[apt.date]) acc[apt.date] = [];
    acc[apt.date].push(apt);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort();

  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  return (
    <div className="cal-agenda">

      {/* ===== HEADER ===== */}
      <div className="cal-agenda-header">
        <span className="cal-agenda-header-month">
          📋 {monthNames[currentDate.getMonth()]}
        </span>
        <span className="cal-agenda-header-range">
          01.{(currentDate.getMonth() + 1).toString().padStart(2, '0')}. - {lastDay.getDate()}.{(currentDate.getMonth() + 1).toString().padStart(2, '0')}.{currentDate.getFullYear()}
        </span>
      </div>

      {/* Termin-Anzahl */}
      <div className="cal-agenda-count">
        {monthAppointments.length} Termine in diesem Monat
      </div>

      {/* Agenda List */}
      <div className="cal-agenda-list">
        {sortedDates.length === 0 ? (
          <div className="cal-agenda-empty">
            <div className="cal-agenda-empty-icon">📭</div>
            <div className="cal-agenda-empty-text">
              Keine Termine in diesem Monat
            </div>
          </div>
        ) : (
          <div className="cal-agenda-groups">
            {sortedDates.map(date => {
              const dateObj = new Date(date);
              const dayAppointments = grouped[date];
              const dayOfWeek = dateObj.getDay() === 0 ? 6 : dateObj.getDay() - 1;
              const today = isToday(dateObj);

              return (
                <div key={date} className="cal-agenda-day">

                  {/* Date Header */}
                  <div className="cal-agenda-day-header">
                    <div className={`cal-agenda-day-badge ${today ? 'cal-agenda-day-badge--today' : ''}`}>
                      <span className="cal-agenda-day-badge-weekday">
                        {weekDays[dayOfWeek].substring(0, 2)}
                      </span>
                      <span className="cal-agenda-day-badge-number">
                        {dateObj.getDate()}
                      </span>
                    </div>

                    <div className="cal-agenda-day-info">
                      <div className={`cal-agenda-day-name ${today ? 'cal-agenda-day-name--today' : ''}`}>
                        {weekDays[dayOfWeek]}, {dateObj.getDate()}. {monthNames[dateObj.getMonth()]}
                      </div>
                      {today && (
                        <span className="cal-agenda-today-tag">Heute</span>
                      )}
                    </div>

                    <div className="cal-agenda-day-count">
                      {dayAppointments.length} {dayAppointments.length === 1 ? 'Termin' : 'Termine'}
                    </div>
                  </div>

                  {/* Appointments */}
                  <div className="cal-agenda-items">
                    {dayAppointments.map(apt => {
                      const catStyle = getCategoryStyle(apt.category);
                      const member = getFamilyMember ? getFamilyMember(apt.person) : null;

                      return (
                        <div
                          key={apt.id}
                          className={`cal-agenda-item cal-agenda-item--${catStyle.cssClass}`}
                          onClick={() => onAppointmentClick(apt)}
                        >
                          <div className="cal-agenda-item-content">
                            <div className="cal-agenda-item-title">
                              {apt.isRecurring && '🔁 '}{apt.title}
                            </div>
                            <div className="cal-agenda-item-meta">
                              <span className="cal-agenda-item-time">
                                🕐 {formatTimeCompact(apt.time)}
                              </span>
                              <span className="cal-agenda-item-sep">•</span>
                              <span className="cal-agenda-item-category">
                                {catStyle.icon} {catStyle.name}
                              </span>
                            </div>
                          </div>
                          <div className="cal-agenda-item-avatar">
                            {member?.avatar || '👤'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AgendaView;